import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, ErrorNote, InlineNote, Spinner } from "./primitives";
import { Mark } from "./Mark";
import { Markdown } from "./Markdown";
import { useActionCeiling } from "./SpendConfirm";
import { api } from "../lib/api";
import { money } from "../lib/format";
import type { Discussion, DiscussionTurn } from "../lib/types";

/**
 * Asking questions about a run that has already finished.
 *
 * The deliverable answers what the engagement was commissioned to answer. It
 * does not answer *"which of these competitors is closest to us"* or *"what did
 * the red team actually object to"* — the questions a deal team has an hour
 * after reading it, which until now cost another run to ask.
 *
 * Two things this page has to be honest about, because a chat window invites
 * the opposite assumption. **What the agent was actually given** — the run's
 * documents go in up to a budget, and a company profile's workspace is 521 KB,
 * so on a large run some of it is readable-on-demand rather than in front of
 * it. And **that answers cost money**, per question, which a text box with a
 * blinking cursor does not otherwise suggest.
 */
export function DiscussRun({ runId }: { runId: string }) {
  const queryClient = useQueryClient();
  const ceiling = useActionCeiling("discussion");
  const [question, setQuestion] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  // A question asked before the reader left is still being answered on the
  // server, which saves it the moment it is asked. Checked back on until its
  // answer lands, since this page holds no request that would bring it.
  const discussion = useQuery({
    queryKey: ["discussion", runId],
    queryFn: () => api.discussion(runId),
    refetchInterval: (query) => (query.state.data?.pending ? 5_000 : false),
  });

  const ask = useMutation({
    mutationFn: () => api.askDiscussion(runId, question),
    // On the page at once, as the server has it: the question, waiting.
    onMutate: () =>
      queryClient.setQueryData<Discussion>(["discussion", runId], (old) =>
        old ? { ...old, pending: { text: question, at: new Date().toISOString() } } : old,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(["discussion", runId], data);
      setQuestion("");
    },
    // The transcript keeps a question that failed, with its reason.
    onError: () => queryClient.invalidateQueries({ queryKey: ["discussion", runId] }),
  });

  const reset = useMutation({
    mutationFn: () => api.clearDiscussion(runId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["discussion", runId] }),
  });

  const turns = discussion.data?.turns ?? [];
  const pending = discussion.data?.pending ?? null;
  const waiting = ask.isPending || pending !== null;

  // Follow the conversation down, as a chat does — but only when a turn
  // arrives, not on every render, or a reader scrolling back up is yanked
  // to the bottom while they are reading.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length]);

  if (discussion.isLoading) return <Spinner />;

  return (
    <div className="flex h-[36rem] flex-col">
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {turns.length === 0 && !pending ? (
          <Opening loaded={discussion.data?.loaded.length ?? 0} />
        ) : (
          <ul className="space-y-4">
            {turns.map((turn, index) => (
              <Exchange key={index} turn={turn} runId={runId} />
            ))}
          </ul>
        )}
        {pending && (
          <ul className={turns.length ? "mt-4" : ""}>
            <Exchange
              turn={{ role: "user", text: pending.text, at: pending.at, cost_usd: 0 }}
              runId={runId}
            />
          </ul>
        )}
        {waiting && (
          <p className="mt-4 text-sm text-ink-2">
            Reading the run…
          </p>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-line px-5 py-3">
        {ask.error && <ErrorNote error={ask.error} />}
        <textarea
          className="field mt-2"
          rows={2}
          placeholder="Ask about this engagement — its findings, its evidence, what it could not establish…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, shift+enter breaks the line — the convention every
            // chat box has, and getting it backwards is the thing people
            // notice first.
            if (e.key === "Enter" && !e.shiftKey && question.trim() && !waiting) {
              e.preventDefault();
              ask.mutate();
            }
          }}
        />
        {/* The row reserves the mark's height whether or not it is showing.
            The mark is taller than the button, so without this the button,
            the context line and "Start over" all shift down when a question
            is asked and back up when it lands — on every question. */}
        <div className="mt-2 flex min-h-[44px] flex-wrap items-center gap-3">
          <Button
            variant="primary"
            disabled={!question.trim() || waiting}
            onClick={() => ask.mutate()}
          >
            {waiting ? "Thinking…" : "Ask"}
          </Button>
          {/*
            The mark draws itself while the answer is being written, which is
            the one thing it is *about*: the dots are already there because
            the run found them, the connections are the agent's work, and the
            finding follows from both. It loops here — the second surface to
            take that, after the front door — on the same argument, that a
            repeating animation is only noise where nobody is waiting. Mounted
            with the pending state, so each question starts the draw again.
          */}
          {waiting && (
            <span role="status" className="flex items-center">
              <Mark size={44} animate loop />
              <span className="sr-only">Working on your question</span>
            </span>
          )}
          <Context
            loaded={discussion.data?.loaded ?? []}
            omitted={discussion.data?.omitted ?? []}
            already={discussion.data?.context_loaded ?? false}
          />
          {/* Before the question, not only after it: a text box with a
              blinking cursor does not say that each answer opens a model
              session with a ceiling of its own (#124). */}
          <span className="text-xs text-muted">
            Each question may use up to{" "}
            <span className="font-mono tabular-nums">
              {ceiling ? money(ceiling.ceiling_usd) : "—"}
            </span>
            {ceiling && !ceiling.charged_to_credit && ", not charged to your credit"}
            {(discussion.data?.cost_usd ?? 0) > 0 && (
              <>
                {" · "}
                <span className="font-mono tabular-nums">
                  {money(discussion.data!.cost_usd)}
                </span>{" "}
                so far
              </>
            )}
          </span>
          {turns.length > 0 && !waiting && (
            <button
              onClick={() => reset.mutate()}
              className="ml-auto text-xs text-ink-2 underline"
              title="Forget this conversation and load the documents again"
            >
              Start over
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Opening({ loaded }: { loaded: number }) {
  return (
    <div className="mx-auto max-w-lg py-10 text-center">
      <p className="text-sm font-medium text-ink">Discuss this engagement</p>
      <p className="mt-2 text-sm text-ink-2">
        Ask about anything in the run — the findings and the evidence behind
        them, what a specialist concluded, what the red team objected to, what
        the work could not establish. The first question loads the run's
        documents; the ones after it do not.
      </p>
      <p className="mt-3 text-xs text-muted">
        {loaded > 0
          ? `${loaded} documents are already in context.`
          : "Answers come from this run's own documents, never from the web."}
      </p>
    </div>
  );
}

/**
 * What the agent was handed.
 *
 * Not decoration: a chat window implies the model has read everything, and on
 * a large run some files are readable-on-demand rather than in front of it. A
 * reader who is not told what is missing believes they have been told
 * everything — the same rule this codebase applies to a locked PDF and a
 * skipped upload.
 */
function Context({
  loaded,
  omitted,
  already,
}: {
  loaded: string[];
  omitted: string[];
  already: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!already) return null;

  return (
    <span className="text-xs text-ink-2">
      <button onClick={() => setOpen(!open)} className="underline">
        {loaded.length} document{loaded.length === 1 ? "" : "s"} in context
        {omitted.length > 0 && `, ${omitted.length} readable on request`}
      </button>
      {open && (
        <span className="mt-1 block max-h-32 overflow-y-auto font-mono text-xs text-muted">
          {loaded.map((name) => (
            <span key={name} className="block truncate">
              {name}
            </span>
          ))}
          {omitted.map((name) => (
            <span key={name} className="block truncate opacity-60">
              {name} — on request
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

function Exchange({ turn, runId }: { turn: DiscussionTurn; runId: string }) {
  if (turn.role === "user")
    return (
      <li className="flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-surface-2 px-3 py-2 text-sm">
          {turn.text}
        </div>
      </li>
    );

  return (
    <li>
      {/* Markdown, because the answer to "compare these four" is a table and
          the whole point of this over a plain text box is that it renders. */}
      <Markdown
        content={turn.text}
        resolveHref={(href) => api.siblingUrl(runId, href)}
      />
      {turn.error && (
        <InlineNote tone="critical" className="mt-1">
          {turn.error}
        </InlineNote>
      )}
    </li>
  );
}
